import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API, SHOW_PRICING } from '../App';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Zap, ArrowRight, Copy, Check, Sparkles, PenLine, Crown, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import EmotionCheckpoint from '../components/EmotionCheckpoint';

// ============ FIRST ALIGNED ACTION ============
// Move from "I understand" to "I act"
// Now includes a natural elevation moment

const FirstAction = () => {
  const [contentIdea, setContentIdea] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [stage, setStage] = useState(0); // 0: loading, 1: reveal, 2: action, 3: elevation, 4: emotion checkpoint
  const [loadingText, setLoadingText] = useState("Création de ton premier contenu...");
  const [showEmotionCheckpoint, setShowEmotionCheckpoint] = useState(false);
  const [isContentExpanded, setIsContentExpanded] = useState(false);
  
  const { token, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  // During beta (SHOW_PRICING=false), skip elevation moment
  // Check if user is on Essentiel (show elevation) or Experience (show activation)
  const isEssentiel = SHOW_PRICING && (user?.subscription_tier === 'essentiel' || user?.subscription_tier === 'free' || !user?.subscription_tier);

  // Determine if already completed (after auth loads)
  const isAlreadyCompleted = !authLoading && user?.first_action_completed === true;

  // Loading text progression (only if not already completed)
  useEffect(() => {
    if (!isLoading || isAlreadyCompleted || authLoading) return;
    
    const texts = [
      "Création de ton premier contenu...",
      "Basé sur ton identité...",
      "Aligné avec ta direction..."
    ];
    
    let index = 0;
    const interval = setInterval(() => {
      index = (index + 1) % texts.length;
      setLoadingText(texts[index]);
    }, 2500);
    
    return () => clearInterval(interval);
  }, [isLoading, isAlreadyCompleted]);

  // Generate first content idea (only if not already completed)
  useEffect(() => {
    if (isAlreadyCompleted || authLoading) return;
    
    const generateFirstContent = async () => {
      try {
        // Get latest content direction for context
        const directionRes = await axios.get(
          `${API}/content/direction/latest`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const direction = directionRes.data?.direction;
        
        // Use signature content idea if available, otherwise generate new
        if (direction?.signature_content_idea) {
          setContentIdea({
            title: direction.signature_content_idea.title,
            concept: direction.signature_content_idea.concept,
            why_it_works: direction.signature_content_idea.why_it_works,
            hook: direction.signature_content_idea.concept, // Full concept without truncation
            cta: "Essaie cette idée dès aujourd'hui",
            source: "signature"
          });
        } else {
          // Generate a quick content idea
          const contentRes = await axios.post(
            `${API}/content/generate`,
            { content_type: "post" },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setContentIdea({
            ...contentRes.data,
            source: "generated"
          });
        }
        
        setIsLoading(false);
        setTimeout(() => setStage(1), 500);
      } catch (err) {
        console.error('First action error:', err);
        // Fallback content
        setContentIdea({
          title: "Ta première publication alignée",
          hook: "Partage ce qui t'a poussée à te lancer dans ce que tu fais aujourd'hui.",
          concept: "Une histoire personnelle qui montre ta transformation — de l'incertitude à la clarté.",
          cta: "Publie cette idée aujourd'hui",
          source: "fallback"
        });
        setIsLoading(false);
        setTimeout(() => setStage(1), 500);
      }
    };

    generateFirstContent();
  }, [token]);

  const copyToClipboard = () => {
    const text = `${contentIdea?.title}\n\n${contentIdea?.hook || contentIdea?.concept}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copié !');
    setTimeout(() => setCopied(false), 2000);
  };

  const goToEmotionCheckpoint = () => {
    setShowEmotionCheckpoint(true);
  };

  const goToDashboard = async () => {
    try {
      // Mark first action as completed before navigating
      await axios.post(
        `${API}/onboarding/complete-first-action`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      console.error('Error completing first action:', err);
      // Continue to dashboard even if marking fails
    }
    navigate('/dashboard');
  };

  // Loading state while auth is loading
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <div className="text-center">
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-16 h-16 mx-auto mb-8 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center"
          >
            <PenLine className="text-[#D4AF37]" size={24} />
          </motion.div>
        </div>
      </div>
    );
  }

  // Show "already completed" screen if step is done
  if (isAlreadyCompleted) {
    return (
      <div className="min-h-screen bg-[#030303]">
        <AnimatePresence mode="wait">
          <motion.div
            key="already-completed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen flex flex-col items-center justify-center px-6 py-12"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              className="w-20 h-20 mb-8 bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded-full flex items-center justify-center"
            >
              <Sparkles className="text-[#D4AF37]" size={32} />
            </motion.div>
            
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.4 }}
              className="text-[#D4AF37]/60 text-xs tracking-[0.2em] uppercase mb-6"
            >
              Parcours de révélation complété
            </motion.p>
            
            <motion.h2
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="text-white text-2xl md:text-4xl text-center mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Tu as déjà passé cette étape.
            </motion.h2>
            
            <motion.p
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="text-[#71717A] text-base text-center max-w-md mb-12"
            >
              Ton parcours de révélation est complété.
              <br />
              Tu peux maintenant accéder à ton espace et créer du contenu aligné.
            </motion.p>
            
            <motion.div
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.5 }}
              className="flex flex-col items-center gap-4"
            >
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-[#D4AF37] hover:bg-[#B8860B] text-black px-10 py-6 text-base font-medium"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid="go-to-dashboard"
              >
                Accéder à mon espace <ArrowRight className="ml-3" size={18} />
              </Button>
              
              <Button
                onClick={() => navigate('/dashboard?tab=content')}
                className="bg-transparent border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 px-8 py-5"
                data-testid="create-content"
              >
                Créer du contenu <Zap className="ml-2" size={16} />
              </Button>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <div className="text-center">
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-16 h-16 mx-auto mb-8 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center"
          >
            <PenLine className="text-[#D4AF37]" size={24} />
          </motion.div>
          <AnimatePresence mode="wait">
            <motion.p
              key={loadingText}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              className="text-white text-base"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {loadingText}
            </motion.p>
          </AnimatePresence>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030303] overflow-hidden">
      <AnimatePresence mode="wait">
        
        {/* Stage 1: Reveal the action */}
        {stage === 1 && (
          <motion.div
            key="reveal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="min-h-screen flex flex-col items-center justify-center px-6 py-12"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="flex items-center gap-3 mb-8"
            >
              <div className="w-10 h-10 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center">
                <Zap className="text-[#D4AF37]" size={18} />
              </div>
              <p className="text-[#D4AF37]/70 text-xs tracking-[0.25em] uppercase">
                Ta première action alignée
              </p>
            </motion.div>
            
            <motion.h1
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="text-white text-2xl md:text-3xl text-center mb-4 max-w-2xl"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Tu comprends. Maintenant, tu agis.
            </motion.h1>
            
            <motion.p
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="text-[#71717A] text-base text-center mb-12 max-w-md"
            >
              Voici une idée de contenu créée spécialement pour toi.
              <br />Prête à publier.
            </motion.p>
            
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.5 }}
            >
              <Button
                onClick={() => setStage(2)}
                className="bg-[#D4AF37] hover:bg-[#B8860B] text-black px-8 py-5 font-medium"
                data-testid="show-content-idea"
              >
                Voir mon idée <ArrowRight className="ml-2" size={18} />
              </Button>
            </motion.div>
          </motion.div>
        )}

        {/* Stage 2: The content idea */}
        {stage === 2 && (
          <motion.div
            key="action"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="min-h-screen flex flex-col items-center justify-center px-6 py-12"
          >
            <motion.p
              initial={{ y: -15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              className="text-[#D4AF37]/70 text-xs tracking-[0.25em] uppercase mb-8"
            >
              Ton contenu prêt à poster
            </motion.p>
            
            {/* Content Card */}
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="w-full max-w-2xl"
            >
              <div className="border border-[#27272A] bg-[#0A0A0A] p-8 relative">
                {/* Title */}
                <h2 
                  className="text-[#D4AF37] text-xl md:text-2xl mb-4 italic pr-10"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  "{contentIdea?.title}"
                </h2>
                
                {/* Hook / Concept - with expand/collapse */}
                <div className="mb-6">
                  <p className={`text-white text-base leading-relaxed ${!isContentExpanded ? 'line-clamp-4' : ''}`}>
                    {contentIdea?.hook || contentIdea?.concept}
                  </p>
                  
                  {/* Show "Voir plus" button if content is long */}
                  {(contentIdea?.hook?.length > 200 || contentIdea?.concept?.length > 200) && (
                    <button
                      onClick={() => setIsContentExpanded(!isContentExpanded)}
                      className="flex items-center gap-1 text-[#D4AF37] text-sm mt-3 hover:text-[#E5C585] transition-colors"
                      data-testid="expand-content-btn"
                    >
                      {isContentExpanded ? (
                        <>
                          <ChevronUp size={16} />
                          Voir moins
                        </>
                      ) : (
                        <>
                          <ChevronDown size={16} />
                          Voir plus
                        </>
                      )}
                    </button>
                  )}
                </div>
                
                {/* Why it works */}
                {contentIdea?.why_it_works && (
                  <div className="border-t border-[#1C1C1C] pt-4 mt-4">
                    <p className="text-[#52525B] text-xs tracking-[0.1em] uppercase mb-2">
                      Pourquoi ça marche pour toi
                    </p>
                    <p className="text-[#71717A] text-sm leading-relaxed">
                      {contentIdea?.why_it_works}
                    </p>
                  </div>
                )}
                
                {/* Copy button */}
                <button
                  onClick={copyToClipboard}
                  className="absolute top-4 right-4 p-2 text-[#52525B] hover:text-[#D4AF37] transition-colors"
                  title="Copier"
                >
                  {copied ? <Check size={18} /> : <Copy size={18} />}
                </button>
              </div>
            </motion.div>
            
            {/* CTA */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.4 }}
              className="text-[#D4AF37] text-sm mt-8 mb-6"
            >
              {contentIdea?.cta || "Publie cette idée aujourd'hui"}
            </motion.p>
            
            {/* Actions */}
            <motion.div
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1, duration: 0.4 }}
              className="flex gap-4"
            >
              <Button
                onClick={copyToClipboard}
                variant="outline"
                className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 px-6 py-5"
              >
                {copied ? <Check className="mr-2" size={16} /> : <Copy className="mr-2" size={16} />}
                Copier l'idée
              </Button>
              
              <Button
                onClick={() => setStage(3)}
                className="bg-[#D4AF37] hover:bg-[#B8860B] text-black px-6 py-5"
                data-testid="go-to-dashboard"
              >
                Continuer <ArrowRight className="ml-2" size={16} />
              </Button>
            </motion.div>
            
            {/* Skip option */}
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.4 }}
              onClick={() => setStage(3)}
              className="mt-8 text-[#52525B] text-sm hover:text-[#71717A] transition-colors"
            >
              Je le ferai plus tard →
            </motion.button>
          </motion.div>
        )}

        {/* Stage 3: Natural Elevation Moment (only for Essentiel users) */}
        {stage === 3 && isEssentiel && (
          <motion.div
            key="elevation"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen flex flex-col items-center justify-center px-6 py-12"
          >
            {/* Opening */}
            <motion.div
              initial={{ y: -10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="text-center max-w-xl mb-2"
            >
              <p 
                className="text-white text-xl md:text-2xl leading-relaxed"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Ce que tu viens de voir…
              </p>
              <p className="text-[#A1A1AA] text-base mt-4">
                c'est déjà un début.
              </p>
            </motion.div>
            
            {/* Pause */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.4 }}
              className="w-8 h-px bg-[#27272A] my-6"
            />
            
            {/* The gap */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.5 }}
              className="text-center max-w-md mb-2"
            >
              <p className="text-[#A1A1AA] text-base leading-relaxed">
                Mais ce n'est pas encore
              </p>
              <p className="text-white text-base leading-relaxed">
                ce que tu pourrais vraiment construire.
              </p>
            </motion.div>
            
            {/* Pause */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1, duration: 0.4 }}
              className="w-8 h-px bg-[#27272A] my-6"
            />
            
            {/* Imagine */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.3, duration: 0.5 }}
              className="text-[#D4AF37] text-lg italic text-center mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Imagine…
            </motion.p>
            
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1.5, duration: 0.5 }}
              className="text-center max-w-md mb-2"
            >
              <p className="text-white text-base leading-relaxed mb-1">
                si chaque contenu que tu crées
              </p>
              <p className="text-white text-base leading-relaxed mb-4">
                était aligné.
              </p>
              <p className="text-[#A1A1AA] text-base leading-relaxed mb-1">
                Pensé.
              </p>
              <p className="text-[#A1A1AA] text-base leading-relaxed">
                Structuré.
              </p>
            </motion.div>
            
            {/* Pause */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.8, duration: 0.4 }}
              className="w-8 h-px bg-[#27272A] my-6"
            />
            
            {/* No more */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2.0, duration: 0.5 }}
              className="text-center mb-2"
            >
              <p className="text-[#71717A] text-base mb-1">
                Plus de confusion.
              </p>
              <p className="text-[#71717A] text-base">
                Plus d'hésitation.
              </p>
            </motion.div>
            
            {/* Pause */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2.3, duration: 0.4 }}
              className="w-8 h-px bg-[#27272A] my-6"
            />
            
            {/* Just you */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2.5, duration: 0.5 }}
              className="text-center mb-2"
            >
              <p className="text-white text-base mb-1">
                Juste toi.
              </p>
              <p className="text-[#D4AF37] text-base mb-1">
                Claire.
              </p>
              <p className="text-[#D4AF37] text-base">
                Positionnée.
              </p>
            </motion.div>
            
            {/* Pause */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2.8, duration: 0.4 }}
              className="w-8 h-px bg-[#27272A] my-6"
            />
            
            {/* And above all */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 3.0, duration: 0.5 }}
              className="text-center mb-2"
            >
              <p className="text-[#A1A1AA] text-base italic mb-2">
                Et surtout…
              </p>
              <p 
                className="text-white text-base"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                guidée à chaque étape.
              </p>
            </motion.div>
            
            {/* Pause */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 3.3, duration: 0.4 }}
              className="w-8 h-px bg-[#27272A] my-6"
            />
            
            {/* The unlock */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 3.5, duration: 0.5 }}
              className="text-[#D4AF37] text-base text-center max-w-md mb-10"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              C'est exactement ce que Vision Lab Expérience débloque.
            </motion.p>
            
            {/* CTAs */}
            <motion.div
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 3.8, duration: 0.4 }}
              className="flex flex-col items-center gap-4"
            >
              {SHOW_PRICING && (
                <Button
                  onClick={() => navigate('/pricing?plan=experience&source=first_action')}
                  className="bg-transparent border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/5 px-8 py-5"
                  data-testid="discover-experience"
                >
                  <Crown className="mr-2" size={16} />
                  Découvrir Vision Lab Expérience
                </Button>
              )}
              
              <button
                onClick={goToEmotionCheckpoint}
                className="text-[#52525B] text-sm hover:text-[#71717A] transition-colors mt-2"
                data-testid="continue-to-dashboard"
              >
                Continuer avec Essentiel →
              </button>
            </motion.div>
          </motion.div>
        )}

        {/* Stage 3: Activation Moment (for Vision Lab Expérience users) */}
        {stage === 3 && !isEssentiel && (
          <motion.div
            key="activation"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen flex flex-col items-center justify-center px-6 py-12"
          >
            {/* Validation badge */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              className="flex items-center gap-2 mb-8"
            >
              <div className="w-8 h-8 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center">
                <Sparkles className="text-[#D4AF37]" size={14} />
              </div>
              <p className="text-[#D4AF37]/60 text-xs tracking-[0.2em] uppercase">
                Vision Lab Expérience
              </p>
            </motion.div>
            
            {/* Opening */}
            <motion.div
              initial={{ y: -10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="text-center max-w-xl mb-2"
            >
              <p 
                className="text-white text-xl md:text-2xl leading-relaxed"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Ce que tu viens de créer…
              </p>
              <p className="text-[#D4AF37] text-base mt-4">
                est déjà aligné.
              </p>
            </motion.div>
            
            {/* Pause */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.4 }}
              className="w-8 h-px bg-[#27272A] my-6"
            />
            
            {/* No more random */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9, duration: 0.5 }}
              className="text-center mb-2"
            >
              <p className="text-[#A1A1AA] text-base mb-4">
                Tu ne postes plus au hasard.
              </p>
              <p 
                className="text-white text-lg"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Tu construis.
              </p>
            </motion.div>
            
            {/* Pause */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.4 }}
              className="w-8 h-px bg-[#27272A] my-6"
            />
            
            {/* Each content */}
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1.4, duration: 0.5 }}
              className="text-center max-w-md mb-2"
            >
              <p className="text-white text-base leading-relaxed mb-4">
                Chaque contenu devient une direction.
              </p>
              <p className="text-white text-base leading-relaxed">
                Chaque mot devient un positionnement.
              </p>
            </motion.div>
            
            {/* Pause */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.7, duration: 0.4 }}
              className="w-8 h-px bg-[#27272A] my-6"
            />
            
            {/* Taking your place */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.9, duration: 0.5 }}
              className="text-[#D4AF37] text-base italic text-center mb-2"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Tu es en train de prendre ta place.
            </motion.p>
            
            {/* Pause */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2.2, duration: 0.4 }}
              className="w-8 h-px bg-[#27272A] my-6"
            />
            
            {/* Continue */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2.4, duration: 0.5 }}
              className="text-center mb-10"
            >
              <p 
                className="text-white text-lg mb-2"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Continue.
              </p>
              <p className="text-[#A1A1AA] text-base">
                C'est comme ça que tout se construit.
              </p>
            </motion.div>
            
            {/* CTA */}
            <motion.div
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 2.7, duration: 0.4 }}
            >
              <Button
                onClick={goToEmotionCheckpoint}
                className="bg-[#D4AF37] hover:bg-[#B8860B] text-black px-8 py-5"
                data-testid="continue-building"
              >
                Accéder à mon espace <ArrowRight className="ml-2" size={16} />
              </Button>
            </motion.div>
          </motion.div>
        )}

        {/* Stage 4: Emotion Checkpoint */}
        {showEmotionCheckpoint && (
          <EmotionCheckpoint
            step="first_action"
            onComplete={goToDashboard}
          />
        )}
        
      </AnimatePresence>
    </div>
  );
};

export default FirstAction;
